import z from 'zod';
import { referralAttributionSchema } from '../referral/referral.attribution.model';
import { referralCodeSchema } from '../referral/referral.code.schema';

/**
 * Where a Lead originated. Used for funnel / attribution analytics
 * and to decide which automated follow-up the sales team should run.
 *
 * - `landing_page`        — submitted from the public landing site (generic CTA).
 * - `landing_demo_form`   — landing site "Book a demo" form. Default for
 *                            anything posted through the public `lead/public`
 *                            endpoint unless overridden.
 * - `sales_hunter`        — manually created by an on-ground sales rep
 *                            (admin-panel).
 * - `referral`            — referred by an existing customer or partner.
 * - `inbound_call`        — picked up the phone / call back form.
 * - `whatsapp`            — incoming WhatsApp conversation.
 * - `marketplace`         — found us through a marketplace listing.
 * - `imported`            — bulk import (CSV / spreadsheet).
 * - `signup`              — self-service app signup wizard (new Org created).
 * - `other`               — catch-all when none of the above apply.
 */
export const leadSourceSchema = z.enum([
  'landing_page',
  'landing_demo_form',
  'sales_hunter',
  'referral',
  'inbound_call',
  'whatsapp',
  'marketplace',
  'imported',
  'signup',
  'other',
]);

export type LeadSource = z.infer<typeof leadSourceSchema>;

/**
 * Structured UTM attribution. Mirrors the `utm_*` query params the
 * landing pages already forward.
 */
export const leadUtmSchema = z.object({
  source: z.string().optional(),
  medium: z.string().optional(),
  campaign: z.string().optional(),
  term: z.string().optional(),
  content: z.string().optional(),
});

export type LeadUtm = z.infer<typeof leadUtmSchema>;

/**
 * Free-form attribution metadata captured alongside `Lead.meta.source`.
 *
 * Server-side fields (`ip`, `userAgent`) are stamped by the public
 * endpoint and never accepted from the client. Client-side fields
 * (`utm`, `referrer`, `landingPath`, `formId`, `campaign`, `medium`)
 * come from the landing-page form.
 *
 * **Marketing attribution only.** It used to also carry `orgId`,
 * `userId` and `conversationId` — identity pointers hidden in an
 * attribution bag, each written by exactly one signup path, so "which
 * Org is this lead" had three possible answers depending on how the lead
 * arrived. Identity now lives on `Lead.parties[]` and
 * `Lead.converted.party`; the first thread lives on
 * `meta.firstTouch.conversationId`.
 */
export const leadSourceMetaSchema = z.object({
  /** UTM-style campaign attribution from query params. */
  utm: leadUtmSchema.optional(),
  /** Free-form campaign identifier (e.g. "spring_2026_riyadh"). */
  campaign: z.string().optional(),
  /** Channel medium ("paid_search", "social", "email", ...). */
  medium: z.string().optional(),
  /** `document.referrer` value at submission time. */
  referrer: z.string().optional(),
  /** Path on the landing site the form was submitted from. */
  landingPath: z.string().optional(),
  /** Identifier of the specific form variant (A/B test, page name, etc.). */
  formId: z.string().optional(),
  /** IP address — stamped by the backend on the public endpoint. */
  ip: z.string().optional(),
  /** `User-Agent` header — stamped by the backend on the public endpoint. */
  userAgent: z.string().optional(),
  /**
   * The raw referral code the client sent (`?r=CODE` on the landing
   * page, the `hero_r` cookie, a signup field). Accepted from the
   * client; the backend resolves it to a `ReferralLink` and writes
   * {@link referral}. Kept even when resolution fails so an unknown or
   * expired code is still visible in the CRM.
   */
  referralCode: referralCodeSchema.optional(),
  /**
   * The first code the same browser saw (`hero_r`) when it differs from
   * `referralCode` (the latest, `hero_r_last`). History only; the code
   * the person converted through owns the lead.
   */
  referralOriginalCode: referralCodeSchema.optional(),
  /**
   * The first-party visitor id (`hero_vid` cookie) the client sent, so
   * the backend can match a `ReferralTracking` hit from the same browser
   * when no code arrived. Accepted from the client.
   */
  referralVisitorId: z.string().trim().max(64).optional(),
  /**
   * Resolved referral attribution — which partner and link brought this
   * lead in, and how the match was made. Server-written only, through
   * `ReferralAttributionService`; never accepted from the client.
   */
  referral: referralAttributionSchema.optional(),
});

export type LeadSourceMeta = z.infer<typeof leadSourceMetaSchema>;
